import { LucideX } from "lucide-react";
import { FocusEvent, Ref, useState } from "react";
import { Kbd } from "~/components/ui/kbd";
import { cn } from "~/utils";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  testId?: string;
  onClear?: () => void;
  showEnterHint?: boolean;
  showClearOnHover?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export const InputWithClear = ({
  className,
  type = "text",
  testId,
  onClear,
  showEnterHint,
  showClearOnHover,
  onBlur,
  onFocus,
  ref,
  ...props
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasFocusOutlineNone = className?.includes("focus:outline-hidden");
  const hasValue = Boolean(props.value);
  const showClearButton = hasValue && Boolean(onClear);
  const showEnterKbd = Boolean(showEnterHint && isFocused);
  const shouldShowClearOnHover = Boolean(showClearOnHover);

  const handleClear = () => {
    onClear?.();
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  return (
    <div className="group relative w-full">
      <input
        ref={ref}
        type={type}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          "flex h-10 w-full rounded-xl border border-secondary-300 bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-secondary-200 focus-visible:outline-hidden focus:shadow-outline-secondary disabled:cursor-not-allowed disabled:opacity-50",
          !hasFocusOutlineNone && "focus-visible:outline-hidden",
          showEnterKbd && !showClearButton && "pr-16",
          showEnterKbd && showClearButton && !shouldShowClearOnHover && "pr-24",
          showEnterKbd &&
            showClearButton &&
            shouldShowClearOnHover &&
            "pr-16 group-hover:pr-24 group-focus-within:pr-24",
          !showEnterKbd &&
            showClearButton &&
            !shouldShowClearOnHover &&
            "pr-10",
          !showEnterKbd &&
            showClearButton &&
            shouldShowClearOnHover &&
            "group-hover:pr-10 group-focus-within:pr-10",
          className,
        )}
        data-testid={testId}
        {...props}
      />
      {showEnterKbd && (
        <Kbd
          aria-hidden="true"
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2",
            showClearButton && !shouldShowClearOnHover && "right-10",
            showClearButton &&
              shouldShowClearOnHover &&
              "group-hover:right-10 group-focus-within:right-10",
          )}
        >
          Enter
        </Kbd>
      )}
      {showClearButton && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            "absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-500 hover:scale-110 focus:scale-110 transition-[opacity,transform]",
            shouldShowClearOnHover &&
              "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto",
          )}
          aria-label="Clear input"
        >
          <LucideX className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
